import {
  useMutation,
  useQuery,
  DocumentNode,
  TypedDocumentNode,
} from '@apollo/client';
import {
  CommandBarButton,
  DefaultButton,
  ITooltipHostStyles,
  IconButton,
  Shimmer,
  ShimmerElementType,
  Stack,
  Text,
  TooltipHost,
} from '@fluentui/react';
import { ToggleButton } from 'common/components/Buttons/ToggleButton';
import { Chip } from 'common/components/Chip';
import { NoDataView } from 'common/components/DataPlaceholders';
import { EntityTagInput } from 'common/types/globalTypes';
import { dateFormat } from 'common/utils/dateFormats';
import { useCallback, useState } from 'react';
import { useStyles } from './index.styles';
import { OperationVariables } from '@apollo/client';

interface EntityTagsSectionProps<
  TAvailableTagsData,
  TAvailableTagsVars extends OperationVariables,
  TEntityTagsData,
  TEntityTagsVars extends OperationVariables,
  TCreateTagData,
  TCreateTagVars extends OperationVariables,
  TDeleteTagData,
  TDeleteTagVars extends OperationVariables
> {
  entityId: string;
  getAvailableTagsGql:
    | DocumentNode
    | TypedDocumentNode<TAvailableTagsData, TAvailableTagsVars>;
  getEntityTagsGql:
    | DocumentNode
    | TypedDocumentNode<TEntityTagsData, TEntityTagsVars>;
  addTagGql: DocumentNode | TypedDocumentNode<TCreateTagData, TCreateTagVars>;
  deleteTagGql:
    | DocumentNode
    | TypedDocumentNode<TDeleteTagData, TDeleteTagVars>;
  extractAvailableTags: (
    data: TAvailableTagsData | undefined
  ) => { id: string; badgeName: string; badgeColor?: string }[];
  extractEntityTags: (data: TEntityTagsData | undefined) => {
    id: string;
    _rowTimestamp: string;
    _tagBadgeName: string;
    _tagBadgeColor?: string;
    _createdDate: string;
    createdByUser: string;
  }[];
  getAvailableTagsVars: (entityId: string) => TAvailableTagsVars;
  getEntityTagsVars: (entityId: string) => TEntityTagsVars;
  createTagsVars: (tags: EntityTagInput[]) => TCreateTagVars;
  deleteTagsVars: (id: string, rowTimestamp: string) => TDeleteTagVars;
  isDisabled?: boolean;
}

export function EntityTagsSection<
  TAvailableTagsData,
  TAvailableTagsVars extends OperationVariables,
  TEntityTagsData,
  TEntityTagsVars extends OperationVariables,
  TCreateTagData,
  TCreateTagVars extends OperationVariables,
  TDeleteTagData,
  TDeleteTagVars extends OperationVariables
>({
  entityId,
  getAvailableTagsGql,
  getEntityTagsGql,
  addTagGql,
  deleteTagGql,
  extractAvailableTags,
  extractEntityTags,
  getAvailableTagsVars,
  getEntityTagsVars,
  createTagsVars,
  deleteTagsVars,
  isDisabled = false,
}: EntityTagsSectionProps<
  TAvailableTagsData,
  TAvailableTagsVars,
  TEntityTagsData,
  TEntityTagsVars,
  TCreateTagData,
  TCreateTagVars,
  TDeleteTagData,
  TDeleteTagVars
>) {
  const styles = useStyles();
  const [toggleView, setToggleView] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const [addNewTag, { loading: loadingAddNewTag }] = useMutation<
    TCreateTagData,
    TCreateTagVars
  >(addTagGql);
  const [deleteTag, { loading: loadingRemoveTag }] = useMutation<
    TDeleteTagData,
    TDeleteTagVars
  >(deleteTagGql);

  const {
    data: tagsData,
    refetch: refetchAvailableTags,
    loading: tagsLoading,
  } = useQuery<TAvailableTagsData, TAvailableTagsVars>(getAvailableTagsGql, {
    variables: getAvailableTagsVars(entityId),
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !entityId,
  });

  const {
    data: entityTagsData,
    refetch: refetchEntityTags,
    loading: entityTagsLoading,
  } = useQuery<TEntityTagsData, TEntityTagsVars>(getEntityTagsGql, {
    variables: getEntityTagsVars(entityId),
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !entityId,
  });

  const refresh = useCallback(() => {
    refetchAvailableTags(getAvailableTagsVars(entityId));
    refetchEntityTags(getEntityTagsVars(entityId));
  }, [
    entityId,
    refetchAvailableTags,
    refetchEntityTags,
    getAvailableTagsVars,
    getEntityTagsVars,
  ]);

  const removeTag = async (id: string, rowTimestamp: string) => {
    await deleteTag({
      variables: deleteTagsVars(id, rowTimestamp),
    });
    setSelectedTags([]);
    if (!loadingRemoveTag) refresh();
  };

  const addTag = async () => {
    const input = selectedTags.map((tagId) => ({
      tagId,
      entityId,
    })) as EntityTagInput[];
    await addNewTag({
      variables: createTagsVars(input),
    });
    setSelectedTags([]);
    if (!loadingAddNewTag) refresh();
  };

  const isSelected = (tagId: string) => selectedTags.includes(tagId);
  const toggleTag = (tagId: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId]
    );
  };

  const calloutProps = { gapSpace: 0 };
  const hostStyles: Partial<ITooltipHostStyles> = {
    root: { display: 'inline-block' },
  };
  return (
    <Stack tokens={{ childrenGap: 15 }} className={styles.tagsContainer}>
      {toggleView ? (
        <Stack tokens={{ childrenGap: 30 }} className={styles.tagsContent}>
          <Stack horizontal horizontalAlign="space-between">
            <Stack
              wrap
              horizontal
              tokens={{ childrenGap: 10 }}
              className={styles.customWidth}
            >
              {extractAvailableTags(tagsData).map((tag) => (
                <ToggleButton
                  key={tag.id}
                  onClick={() => toggleTag(tag.id)}
                  toggled={isSelected(tag.id)}
                  text={tag.badgeName}
                  iconName={isSelected(tag.id) ? 'Accept' : 'Add'}
                  color={isSelected(tag.id) ? tag.badgeColor : undefined}
                  disabled={isDisabled}
                />
              ))}
              {extractAvailableTags(tagsData).length === 0 && !tagsLoading && (
                <Text>No available tags to add!</Text>
              )}
            </Stack>
            <IconButton
              iconProps={{ iconName: 'Cancel' }}
              className={styles.closeButton}
              onClick={() => {
                setToggleView(false);
                setSelectedTags([]);
              }}
              ariaLabel="Close"
              disabled={isDisabled}
            />
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 20 }}>
            <CommandBarButton
              iconProps={{ iconName: 'Accept' }}
              disabled={
                selectedTags.length === 0 || loadingAddNewTag || isDisabled
              }
              text="Add tags"
              onClick={addTag}
              className={styles.commandBarButton}
            />
            <CommandBarButton
              iconProps={{ iconName: 'Cancel' }}
              disabled={selectedTags.length === 0 || isDisabled}
              text="Cancel"
              onClick={() => setSelectedTags([])}
              className={styles.commandBarButton}
            />
          </Stack>
        </Stack>
      ) : (
        <Stack horizontal horizontalAlign="end">
          <DefaultButton
            iconProps={{ iconName: 'Add' }}
            text="Add Tags"
            onClick={() => setToggleView(true)}
            disabled={isDisabled}
          />
        </Stack>
      )}

      {entityTagsLoading ? (
        <Shimmer
          shimmerElements={[
            { type: ShimmerElementType.line, width: 100, height: 40 },
          ]}
        />
      ) : (
        <Stack
          wrap
          horizontal
          tokens={{ childrenGap: 10 }}
          className={styles.customWidth}
        >
          {extractEntityTags(entityTagsData).map((tag) => (
            <TooltipHost
              key={tag.id}
              content={`${tag.createdByUser} / ${dateFormat(tag._createdDate)}`}
              calloutProps={calloutProps}
              styles={hostStyles}
            >
              <Chip
                text={tag._tagBadgeName}
                color={tag._tagBadgeColor}
                onClick={() => removeTag(tag.id, tag._rowTimestamp)}
                disabled={loadingRemoveTag || isDisabled}
              />
            </TooltipHost>
          ))}
        </Stack>
      )}
      {extractEntityTags(entityTagsData).length === 0 && !entityTagsLoading && (
        <NoDataView title="No tags available!" show />
      )}
    </Stack>
  );
}
